import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, ShieldCheck } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/report", label: "Issue Report" },
  { to: "/contact", label: "Contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/60 bg-cream/80 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <Link
            to="/"
            data-testid="nav-logo"
            className="group flex items-center gap-2.5"
            onClick={() => setOpen(false)}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 transition-colors duration-300 group-hover:bg-blue-700">
              <ShieldCheck className="h-4 w-4 text-white" />
            </span>
            <span className="font-serif text-xl font-semibold tracking-tight text-slate-900">
              EY Tech <span className="text-slate-400">LLC</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-300 ${
                    isActive ? "text-blue-600" : "text-slate-600 hover:text-slate-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/report"
              data-testid="nav-get-audit-button"
              className="group flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-md"
            >
              Get Audit
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="text-slate-900 md:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-cream px-6 pb-10 pt-28 md:hidden"
          >
            <nav className="flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4, ease: "easeOut" }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block border-b border-slate-200 py-5 font-serif text-3xl font-medium tracking-tight ${
                        isActive ? "text-blue-600" : "text-slate-900"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <Link
              to="/report"
              data-testid="nav-mobile-get-audit-button"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-4 text-sm font-semibold text-white"
            >
              Get Audit — $99
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
